import { formatDate } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AgGridAngular } from "ag-grid-angular";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
} from "ag-grid-community";
import { ContractApiService } from "src/app/core/api/contract-api.service";
import { Contract } from "src/app/core/models/contract";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ContractActionsComponent } from "./components/contract-actions/contract-actions.component";
import { ToastrService } from "ngx-toastr";
import { SpinnerService } from "src/app/core/services/spinner.service";
import { finalize } from "rxjs";
import { NoRowsOverlayComponent } from "ag-grid-community/dist/types/src/rendering/overlays/noRowsOverlayComponent";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ColumnApiModule,
    ColumnAutoSizeModule
]);


interface ColumnVisibility {
    field: string;
    headerName: string;
    visible: boolean;
}

@Component({
    selector: "app-excel-info",
    standalone: true,
    imports: [
        AgGridAngular,
        TranslatePipe,
        RouterOutlet,
        CommonModule,
        FormsModule,
    ],
    templateUrl: "./contracts-overview.component.html",
    styleUrl: "./contracts-overview.component.scss",
})
export class ContractsOverviewComponent implements OnInit {
    public contractTableTitles: any = {};
    public contractList: Contract[] = [];
    public colDefs: ColDef<Contract>[] = [];
    public columnVisibility: ColumnVisibility[] = [];
    public showColumnPanel = false;
    private gridApi!: GridApi;
    public gridContext = {
        componentParent: this,
    };

    ngOnInit(): void {
        this.translateService.stream("contracts").subscribe((res) => {
            this.contractTableTitles = res;
            this.initializeColumnDefs();
        });
        this.loadContractList();
    }

    constructor(
        private translateService: TranslateService,
        private contractApiService: ContractApiService,
        private router: Router,
        private toastrService: ToastrService,
        private spinner: SpinnerService
    ) {}

    defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
        suppressColumnVirtualisation: true,
        suppressSizeToFit: true,
    };

    public loadContractList() {
        this.spinner.show();
        this.contractApiService
            .getAllContracts()
            .pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: (result) => {
                    this.contractList = result;
                },
            });
    }

    public directToConsultaantProfile(data: Contract) {
        this.router.navigate([`/search/profile/edit/${data.profileId}`]);
    }

    public directToTask(data: Contract) {
        this.router.navigate([`/tasks/${data.taskId}`]);
    }

    public directToStakeholderList(data: Contract) {
        if (data.customerId) {
            this.router.navigate([`/customers/${data.customerId}`]);
        } else {
            this.translateService
                .get("contracts.noCustomers")
                .subscribe((string) => this.toastrService.info(string));
        }
    }

    private initializeColumnDefs() {
        this.colDefs = [
            {
                field: "taskSmall.title",
                headerName: this.contractTableTitles.title,
                flex: 1,
                valueGetter: (params) => params.data?.taskSmall?.title,
                hide: false,
            },
            {
                field: "fullname",
                headerName: this.contractTableTitles.fullName,
                filter: "agTextColumnFilter",
                flex: 1,
                hide: false,
            },
                        {
                field: "customerName",
                headerName: this.contractTableTitles.customerName,
                filter: "agTextColumnFilter",
                minWidth: 120,
                hide: false,
            },
            {
                field: "customerProfiles.firstname" as any,
                headerName: this.contractTableTitles.customerProfileName,
                flex:1,
                valueGetter: (params) => {
                    if (params.data?.customerProfiles?.length > 0) {
                        return `(${params.data?.customerProfiles?.length})
                   ${params.data?.customerProfiles
                       ?.map((cp) => cp.firstname + " " + cp.surname)
                       .join(", ")} `;
                    }
                    return "";
                },
                hide: false,

            },
                        {
                field: "startDate",
                headerName: this.contractTableTitles.startDate?.toString(),
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                width:120,
                sort: "desc",
                hide: false,
            },
            {
                field: "endDate",
                headerName: this.contractTableTitles.endDate?.toString(),
                valueFormatter: (params) =>
                    formatDate(params.value, "dd-MM-yyyy", "en"),
                filter: false,
                hide: false,
                width:120
            },
            {
                field: "costPrice",
                headerName: this.contractTableTitles.costPrice,
                filter: false,
                minWidth: 100,
                maxWidth: 120,
                hide: false,
            },
            {
                field: "soldPrice",
                headerName: this.contractTableTitles.soldPrice,
                filter: false,
                minWidth: 100,
                maxWidth: 120,
                hide: false,
            },
            {
                field: "margin",
                headerName: this.contractTableTitles.margin,
                filter: false,
                minWidth: 100,
                maxWidth: 120,
                hide: false,
            },
            {
                field: "currency",
                headerName: this.contractTableTitles.currency,
                flex:1,
                hide: false,
                resizable: false,
                suppressMovable: true,
            },


            {
                field: "actions" as any,
                headerName: this.contractTableTitles.actions,
                cellRenderer: ContractActionsComponent,
                filter: false,
                sortable: false,
                width: 130,
                pinned: "right",
                hide: false,
                resizable: false,
                lockPinned: true,
                lockPosition: true,
            },
        ];

        // Initialize column visibility tracking
        this.columnVisibility = this.colDefs.map((col) => ({
            field: col.field || "",
            headerName: col.headerName || "",
            visible: true,
            flex: 1,
        }));
    }

    public onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.loadInitialData();
    }

    loadInitialData(): void {
        this.gridApi.setGridOption("rowData", this.contractList);
    }

    public toggleColumnPanel() {
        this.showColumnPanel = !this.showColumnPanel;
    }

    public toggleColumnVisibility(columnField: string) {
        const columnState = this.columnVisibility.find(
            (col) => col.field === columnField
        );
        if (columnState && this.gridApi) {
            columnState.visible = !columnState.visible;
            this.gridApi.setColumnsVisible([columnField], columnState.visible);
        }
    }

    public showAllColumns() {
        if (this.gridApi) {
            this.columnVisibility.forEach((col) => {
                col.visible = true;
            });
            const allFields = this.columnVisibility.map((col) => col.field);
            this.gridApi.setColumnsVisible(allFields, true);
        }
    }

    public hideAllColumns() {
        if (this.gridApi) {
            this.columnVisibility.forEach((col) => {
                // Keep title and actions
                col.visible = col.field === "taskSmall.title" || col.field === "actions";
            });

            const allFieldsExceptTask = this.columnVisibility
                .filter((col) => col.field !== "taskSmall.title" && col.field !== "actions")
                .map((col) => col.field);

            this.gridApi.setColumnsVisible(allFieldsExceptTask, false);
            this.gridApi.setColumnsVisible(["taskSmall.title", "actions"], true);
        }
    }

    public onRowClicked(event: { data: { id: any } }) {
        const contractId = event.data.id;
        this.router.navigate([`contract/${contractId}/details`]);
    }
}
